import Typography from "@mui/material/Typography";
import { CalendarChangeParams } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CustomersByEntityDropdown } from "../CustomersByEntityDropdown";
import { useAssetsBySubstation } from "../data/Assets/useAssetsBySubstation";
import { useWorkorderCreation } from "../data/Workorders/useWorkorderCreation";
import { ExtraDetails, KeyValue } from "../ExtraDetails";
import { Card } from "../shared/Card";
import { getDateObject, getEndOfDay, getStartOfDay } from "../shared/DateConversions";
import { FormContainer } from "../shared/FormLayout/FormContainer";
import { FormRow } from "../shared/FormLayout/FormRow";
import { Asset } from "../shared/interfaces/Asset";
import { Attribute } from "../shared/interfaces/Attribute";
import { Customer } from "../shared/interfaces/Customer";
import { WorkflowV1 } from "../shared/interfaces/WorkflowV1";
import { WorkflowV2 } from "../shared/interfaces/WorkflowV2";
import { NewWorkOrder } from "../shared/interfaces/WorkOrder";
import { InternationalDatepicker } from "../shared/InternationalDatepicker";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { ProgressButton } from "../shared/ProgressButton";
import { useUpdateAccessNotes } from "./useUpdateAccessNotes";
import WorkorderLocationAndWorkflow from "./WorkorderComponents/WorkorderLocationAndWorkflow";

interface Props {
  open?: boolean;
  onClose: () => void;
  onWorkorderCreated: (workorderId: number) => void;
  customer: Customer;
  customerSelected: boolean;
  prepopulatedFromWorkorder?: Partial<NewWorkOrder>;
}

export function WorkorderCreate(props: Props) {
  const { t } = useTranslation();

  const formMethods = useForm();

  const workorderCreation = useWorkorderCreation<Partial<NewWorkOrder>>({
    onSuccess: onWorkorderCreationSuccess,
    onError: onWorkorderCreationError,
  });

  const [showErrorToast, setShowErrorToast] = useState(false);
  const [switchgearErrorToastMessage, setSwitchgearErrorToastMessage] = useState<string | undefined>(undefined);

  const sanitisedPrepopulatedWorkorderTemplate = useMemo(() => {
    if (props.prepopulatedFromWorkorder) {
      const newWorkorderObjectKeys: (keyof NewWorkOrder)[] = [
        "customerId",
        "customerName",
        "clientId",
        "projectCode",
        "description",
        "longDescription",
        "substationId",
        "substationName",
        "substationId",
        "assetIds",
        "workflowId",
        "workflowDetail",
        "workflowRevisionNo",
        "extraDetail",
        "accessNotes",
        "populateAccessNotes",
      ];

      const workorder = Object.fromEntries(
        newWorkorderObjectKeys
          .filter((key) => props.prepopulatedFromWorkorder && key in props.prepopulatedFromWorkorder)
          .map((key) => [key, props.prepopulatedFromWorkorder?.[key]]),
      );

      // Default duplicate work order target dates to today
      workorder.targetStartDate = getStartOfDay(new Date());
      workorder.targetCompletionDate = getEndOfDay(new Date());

      return workorder;
    } else return undefined;
  }, [props.prepopulatedFromWorkorder]);

  const [newWorkorder, setNewWorkorder] = useState<Partial<NewWorkOrder>>(sanitisedPrepopulatedWorkorderTemplate || {});
  const selectedSubstationId = newWorkorder.substationId ?? 0;
  const { data: substationAssets } = useAssetsBySubstation(selectedSubstationId);

  const { updateAccessNotes } = useUpdateAccessNotes(newWorkorder, setNewWorkorder);

  function onWorkorderCreationSuccess(id: number) {
    props.onWorkorderCreated(id);
  }

  function onWorkorderCreationError() {
    setShowErrorToast(true);
  }

  useEffect(() => {
    setNewWorkorder((prevState) => ({
      ...prevState,
      customerId: props.customer.id,
      customerName: props.customer.name,
    }));
  }, [props.customer]);

  function handleHide() {
    setNewWorkorder({});
    props.onClose();
  }

  function handleInputChange(event: any) {
    const name = event.target.name;
    const value = event.target.value;

    setNewWorkorder({ ...newWorkorder, [name]: value });
  }

  const handleCustomerChange = useCallback((customer: Customer | undefined) => {
    customer &&
      setNewWorkorder((prevState) => ({
        ...prevState,
        customerId: customer.id,
        customerName: customer.name,
      }));
  }, []);

  function handleExtraDetailsChange(extraDetail: KeyValue[], populateAccessNotes?: boolean) {
    if (populateAccessNotes != null) {
      setNewWorkorder({
        ...newWorkorder,
        extraDetail: extraDetail,
        populateAccessNotes: populateAccessNotes,
      });
      return;
    }
    setNewWorkorder({ ...newWorkorder, extraDetail: extraDetail });
  }

  function handleSubstationChange(substationId?: number, _substationName?: string, attributes?: Attribute[]) {
    setNewWorkorder({
      ...newWorkorder,
      substationId: substationId,
      assetIds: undefined,
      workflowId: undefined,
    });
    updateAccessNotes(attributes);
  }

  function handleSwitchgearChange(assets?: Asset[]) {
    if (assets == null || assets.length === 0) {
      setNewWorkorder({
        ...newWorkorder,
        assetIds: undefined,
        workflowId: undefined,
      });
    } else {
      setNewWorkorder({
        ...newWorkorder,
        assetIds: assets.map((asset) => asset.id),
        workflowId: undefined,
      });
    }
  }

  function handleWorkflowChange(workflow: WorkflowV1 | WorkflowV2) {
    setNewWorkorder({
      ...newWorkorder,
      workflowId: workflow.id,
      workflowDetail: workflow.workflowDetail,
      workflowRevisionNo: workflow.revisionNo,
    });
  }

  function handleTargetStartDateChange(e: CalendarChangeParams | undefined) {
    const targetStartDate = e?.value ? getStartOfDay(e?.value as Date) : undefined;
    let targetCompletionDate = newWorkorder.targetCompletionDate;

    if (!targetCompletionDate && targetStartDate) targetCompletionDate = targetStartDate;

    setNewWorkorder({
      ...newWorkorder,
      targetStartDate: targetStartDate,
      targetCompletionDate: targetCompletionDate,
    });
  }

  function handleTargetCompletionDateChange(e: CalendarChangeParams | undefined) {
    const targetCompletionDate = e?.value ? getEndOfDay(e?.value as Date) : undefined;
    let targetStartDate = newWorkorder.targetStartDate;

    if (!targetStartDate && targetCompletionDate) targetStartDate = targetCompletionDate;

    setNewWorkorder({
      ...newWorkorder,
      targetStartDate: targetStartDate,
      targetCompletionDate: targetCompletionDate,
    });
  }

  const handleCreateButtonClick = formMethods.handleSubmit(() => {
    if (!newWorkorder.workflowId) {
      formMethods.setError("workflow", {
        type: "required",
        message: t("workorders.validation.workflowRequired"),
      });
    }
    setSwitchgearErrorToastMessage(undefined);
    workorderCreation.mutate(newWorkorder);
  });

  const header = <Typography variant="h5" component="p">{`${t("workorders.newWorkorder")} `}</Typography>;
  const footer = (
    <div className="d-flex flex-row align-items-center justify-content-end">
      <ProgressButton
        onClick={handleCreateButtonClick}
        disabled={workorderCreation.isLoading}
        loading={workorderCreation.isLoading}
      >
        {" "}
        {t("common.create")}{" "}
      </ProgressButton>
      <div>
        <Button type="button" variant="secondary" onClick={handleHide} disabled={workorderCreation.isLoading}>
          {t("common.cancel")}{" "}
        </Button>
      </div>
    </div>
  );

  function textInputTemplate(key: keyof NewWorkOrder, maxLength?: number) {
    return (
      <InputText
        key={`input-${key}`}
        name={key}
        value={newWorkorder[key]?.toString() || ""}
        onChange={handleInputChange}
        maxLength={maxLength}
      />
    );
  }

  function textAreaInputTemplate(key: keyof NewWorkOrder) {
    return (
      <InputTextarea
        key={key}
        name={key}
        value={newWorkorder[key]?.toString()}
        onChange={handleInputChange}
        autoResize
        className="w-100"
      />
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Dialog
        header={header}
        visible={props.open}
        onHide={handleHide}
        breakpoints={{ "1960px": "75vw", "1300px": "90vw" }}
        style={{ width: "50vw" }}
        footer={footer}
      >
        <Container fluid>
          <Row>
            <Col xl={6} className="px-0">
              <Card title={t("workorders.workorderDetails")} className="mb-3 ">
                <FormContainer>
                  <FormRow
                    required
                    label={t("customer.customer")}
                    value={
                      props.customerSelected ? (
                        props.customer.name
                      ) : (
                        <CustomersByEntityDropdown
                          selectedCustomerId={newWorkorder.customerId}
                          entity="workorder"
                          onSelect={handleCustomerChange}
                          hideLabel={true}
                          register={formMethods.register}
                          formState={formMethods.formState}
                          setValue={formMethods.setValue}
                          displaySingleCustomerName
                        />
                      )
                    }
                  />
                  <FormRow label={t("workorders.clientReference")} value={textInputTemplate("clientId", 50)} />
                  <FormRow label={t("common.projectCode")} value={textInputTemplate("projectCode", 50)} />
                  <FormRow label={t("common.summary")} value={textInputTemplate("description", 200)} />
                  <FormRow label={t("common.detailedDescription")} value={textAreaInputTemplate("longDescription")} />
                  <FormRow
                    label={t("workorders.targetStartDate")}
                    value={
                      <InternationalDatepicker
                        value={getDateObject(newWorkorder.targetStartDate)}
                        maxDate={getDateObject(newWorkorder.targetCompletionDate)}
                        onChange={handleTargetStartDateChange}
                        showIcon
                      />
                    }
                  />
                  <FormRow
                    label={t("workorders.targetCompletionDate")}
                    value={
                      <InternationalDatepicker
                        value={getDateObject(newWorkorder.targetCompletionDate)}
                        minDate={getDateObject(newWorkorder.targetStartDate)}
                        onChange={handleTargetCompletionDateChange}
                        showIcon
                      />
                    }
                  />
                </FormContainer>
              </Card>
            </Col>
            <Col lg={6}>
              <WorkorderLocationAndWorkflow
                workorder={newWorkorder}
                editingWorkorder
                isLoading={false}
                substationChildAssets={substationAssets}
                selectedSubstationChildAssets={substationAssets?.filter((asset) =>
                  newWorkorder.assetIds?.includes(asset.id),
                )}
                onSubstationChange={handleSubstationChange}
                onAssetsChange={handleSwitchgearChange}
                onWorkflowChange={handleWorkflowChange}
              />

              <Card title={t("workorders.extraDetails")} className="mb-3">
                <ExtraDetails data={newWorkorder.extraDetail} editable onEdit={handleExtraDetailsChange} />
              </Card>
              <div>
                {showErrorToast && (
                  <MessageToast
                    text={t("common.dataCreateError", {
                      val: "$t(workorders.workorder)",
                    })}
                    mode={ToastMode.Error}
                    show={showErrorToast}
                    autoHide
                  ></MessageToast>
                )}
                {switchgearErrorToastMessage && (
                  <MessageToast
                    text={switchgearErrorToastMessage}
                    mode={ToastMode.Error}
                    show={switchgearErrorToastMessage != null}
                  ></MessageToast>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </Dialog>
    </FormProvider>
  );
}
